import PropTypes from 'prop-types'
import React from 'react'
import {connect} from 'react-redux'
import {Link, withRouter} from 'react-router-dom'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'

// Components
import SubscriptionLocked from 'app/views/components/Subscription/SubscriptionLocked'
import OutageAlert from 'app/views/components/OutageAlert'

// Containers
import BrowserSkillArea from 'app/views/containers/BrowserSkillArea'
import CodeReview from 'app/views/containers/CodeReview'
import Article from 'app/views/containers/Article'

import LessonUI from 'app/views/content/LessonUI'
import HackingChallengeUI from 'app/views/content/HackingChallengeUI'
import CodingChallengeUI from 'app/views/content/CodingChallengeUI'

// Redux
import {getContentById} from 'app/state/modules/content'
import {resetProxyState} from 'app/state/modules/proxy'
import {replReset} from 'app/state/modules/repl'

// Settings
import settings from 'settings'

// Utils
import analytics from 'app/views/utils/analytics'
import {api} from 'app/views/utils/api'
import isMobileDevice from 'app/views/utils/mobile'
import ModalPreferLanguages from 'app/views/components/Modals/ModalPreferLanguages'

const propTypes = {
  idToken: PropTypes.string, // mapStateToProps
  baseUrl: PropTypes.string, // mapStateToProps
  match: PropTypes.object.isRequired, // withRouter
}
const defaultProps = {
  browserUrl: '',
}

class ContentDetail extends React.Component {
  constructor(props) {
    super(props)
    this.isDemo = this.props.match.path === '/demo'
    if (this.isDemo) {
      this.contentId = 'ffaf1a2c-563c-49ea-94f9-a9be559a21b9'
    } else {
      this.contentId = this.props.match.params.id
    }
    this.trackProgress = this.trackProgress.bind(this)
    this.handleClickBack = this.handleClickBack.bind(this)
    this.handleClickCircle = this.handleClickCircle.bind(this)
    this.handleClickDone = this.handleClickDone.bind(this)
    this.handleClickNext = this.handleClickNext.bind(this)
    this.renderContentType = this.renderContentType.bind(this)
    this.props.resetProxyState()
  }

  componentDidMount() {
   
    if (this.isDemo) {
       // call the onDemoShown handler to tell the main app to close the cookie notice automatically
      this.props.onDemoShown && this.props.onDemoShown()
    }
    
    this.props.getContentById(this.contentId)
    analytics.page('page-lesson', {
      id: this.contentId,
      contentType: 'course',
    })
  }

  trackProgress(stepNumber) {
    if (this.props.match && this.props.match.params && this.contentId && this.props.profile && this.props.profile.user_id) {
      api({
        method: 'post',
        url: `${settings.urls.hacker}/user/${this.props.profile.user_id}/content/${this.contentId}`,
        idToken: this.props.idToken,
        data: {
          step_number: stepNumber,
        },
      })
        .catch((error) => {
          console.error('error posting progress: ', error)
        })
    }
  }

  handleClickBack(stepIndex) {
    this.trackProgress(stepIndex)
    analytics.track('click-step-back', {
      lessonId: this.contentId,
      taskId: this.templateId(),
      stepIndex,
    })
  }

  templateId() {
    const searchParams = new URLSearchParams(this.props.location.search)
    return searchParams.get('template_override') || this.props.contentDetails[this.contentId].ecs_task_definition;
  }

  handleClickCircle(stepIndex) {
    this.trackProgress(stepIndex)
    analytics.track('click-step-dotnav', {
      lessonId: this.contentId,
      taskId: this.templateId(),
      stepIndex,
    })
  }

  handleClickDone(stepIndex) {
    this.trackProgress(stepIndex)
    analytics.track('click-step-done', {
      lessonId: this.contentId,
      taskId: this.templateId(),
    })
  }

  handleClickNext(stepIndex) {
    this.trackProgress(stepIndex)
    analytics.track('click-step-next', {
      lessonId: this.contentId,
      taskId: this.templateId(),
      stepIndex,
    })
  }

  renderContentType() {
    const content = this.props.contentDetails[this.contentId]
    const searchParams = new URLSearchParams(this.props.location.search)
    let useK8s = this.isDemo ? this.props.flags.useKubernetesDemo : content.k8s_enabled

    // allow override of k8s setting with querystring param
    const k8s_override = searchParams.get('k8s_override')
    if ( k8s_override ) {
      if (k8s_override === 'false') useK8s = false
      if (k8s_override === 'true') useK8s = true
    }

    if (useK8s) {
      if (content.content_type === 'lesson') {
        return (
            <LessonUI
              isDemo={this.isDemo}
              contentId={this.contentId}
              contentDetails={{
                ...content,
                sandboxTemplateName: this.templateId(),
              }}
              sandboxTemplateName={this.templateId() || ''}
              mainTabAppNameOverride={searchParams.get('app_name_override')}
            />
        )
      }
      if (content.content_type === 'hacking_challenge') {
        return (
          <HackingChallengeUI
            contentId={this.contentId}
            contentDetails={content}
            sandboxTemplateName={this.templateId() || ''}
          />
        )
      }
      if (content.content_type === 'coding_challenge' || content.content_type === 'mobile') {
        return (
          <CodingChallengeUI
            contentId={this.contentId}
            contentDetails={content}
            sandboxTemplateName={this.templateId()}
          />
        )
      }
    }

    switch (content.content_type) {
      case 'coding_challenge':
        return (
          <>
            <CodeReview
              content={content}
              handleClickBack={(stepIndex) => { this.handleClickBack(stepIndex) }}
              handleClickCircle={(stepIndex) => { this.handleClickCircle(stepIndex) }}
              handleClickDone={(stepIndex) => { this.handleClickDone(stepIndex) }}
              handleClickNext={(stepIndex) => { this.handleClickNext(stepIndex) }}
              useK8s={useK8s}
            />
          </>
        )
      case 'article':
        return (
          <Article
            content={content}
            handleClickBack={(stepIndex) => { this.handleClickBack(stepIndex) }}
            handleClickCircle={(stepIndex) => { this.handleClickCircle(stepIndex) }}
            handleClickDone={(stepIndex) => { this.handleClickDone(stepIndex) }}
            handleClickNext={(stepIndex) => { this.handleClickNext(stepIndex) }}
          />
        )
      case 'mobile':
        return (
          <CodeReview
            content={content}
            handleClickBack={(stepIndex) => { this.handleClickBack(stepIndex) }}
            handleClickCircle={(stepIndex) => { this.handleClickCircle(stepIndex) }}
            handleClickDone={(stepIndex) => { this.handleClickDone(stepIndex) }}
            handleClickNext={(stepIndex) => { this.handleClickNext(stepIndex) }}
          />
        )
      default:
        return (
          <BrowserSkillArea
            handleClickBack={(stepIndex) => { this.handleClickBack(stepIndex) }}
            handleClickCircle={(stepIndex) => { this.handleClickCircle(stepIndex) }}
            handleClickDone={(stepIndex) => { this.handleClickDone(stepIndex) }}
            handleClickNext={(stepIndex) => { this.handleClickNext(stepIndex) }}
            title={content.title}
            description={content.description}
            steps={content.steps}
            tabs={content.tabs}
            ecsTaskDefinition={this.templateId()}
            currentProgress={content.current_progress}
            hasTest={content.has_test}
            appType={content.app_type}
            contentType={content.content_type}
            lessonCompletionURL="/"
            lessonCompletionText="Start your next lesson"
            userId={this.props.profile.user_id}
            mainTabAppNameOverride={searchParams.get('app_name_override')}
          />
        )
    }
  }

  render() {
    const content = this.props.contentDetails[this.contentId]
    if (
      !Object.keys(this.props.contentDetails).includes(this.contentId)
      || (
        (!this.isDemo && this.props.license === null)
      )
    ) {
      return (
        <div className="uk-section uk-text-center">
          <div className="uk-container">
            <div data-uk-spinner="" />
          </div>
        </div>
      )
    }
    if ((content.requires_subscription === true && this.props.license === false)
      || (content.requires_subscription === true && content.license_out_of_scope)
      || (content.requires_subscription === true && content.is_expired)
    ) {
      return (
        <SubscriptionLocked
          isExpired={content.is_expired}
          licenseOutOfScope={content.license_out_of_scope}
          contentType={content.content_type}
        />
      )
    }

    if (isMobileDevice()) {
      return (
        <div className="uk-section">
          <div className="uk-container uk-text-center">
            <h3 className="uk-text-bold">Looks like you're on mobile...</h3>
            <p>
              We're glad you're checking out a lesson while on the go, but our tools are not
              available on mobile.
            </p>
            <Link className="uk-button uk-button-primary" to="/">Back To Dashboard</Link>
          </div>
        </div>
      )
    }
    return (
      <>
        {this.renderContentType()}
        <OutageAlert />
        {!this.isDemo && <ModalPreferLanguages />}
      </>
    )
  }
}

ContentDetail.propTypes = propTypes
ContentDetail.defaultProps = defaultProps

const mapStateToProps = (state) => ({
  // auth
  idToken: state.auth.user.idToken,

  // content
  contentDetails: state.content.contentDetails,

  // hacker
  license: state.hacker.license,
  profile: state.hacker.profile,
})

const mapDispatchToProps = (dispatch) => ({
  getContentById: (contentId) => dispatch(getContentById(contentId)),
  resetProxyState: () => dispatch(resetProxyState()),
  replReset: () => dispatch(replReset()),
})

export default withLDConsumer()(withRouter(connect(mapStateToProps, mapDispatchToProps)(ContentDetail)))
